import {
  Button,
  CloseButton,
  Dialog,
  HStack,
  Portal,
  RadioGroup,
  Text,
} from '@chakra-ui/react';
import type { CustomerDetailsType, PhoneNumberType } from '@customTypes/customer';
import api from '@services/api';
import { formatPhoneNumber } from '@utils/formats';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';

type Props = {
  handleClose: () => void,
  item: PhoneNumberType,
};

const RADIO_ITEMS = [
  {
    label: 'Sim',
    value: 'YES',
  },
  {
    label: 'Não',
    value: 'NO',
  },
];

const ReportPhoneNumberDialog = ({ handleClose, item }: Props) => {
  const location = useLocation();
  const customerInteractionId = location.state.customerInteractionId;
  const customer: CustomerDetailsType = location.state.customer || {};

  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [hasWhatsapp, setHasWhatsapp] = useState(item.has_whatsapp ? 'YES' : 'NO');
  const [talksWithCustomer, setTalksWithCustomer] = useState(item.talks_with_customer ? 'YES' : 'NO');

  const onReportClick = async () => {
    setLoading(true);
    const params = {
      phone_number: item.phone_number,
      talks_with_customer: talksWithCustomer === 'YES',
      has_whatsapp: hasWhatsapp === 'YES',
      customer_id: customer.id,
    };

    try {
      const response = await api.post('/customer/report_phone_number', {
        ...params,
      });

      if (response?.status === 200) {
        toast.success('Número de telefone reportado com sucesso.');
        navigate('', {
          replace: true,
          relative: 'path',
          state: {
            customer: {
              ...customer,
              phone_numbers: customer.phone_numbers.map((phone) => (
                phone.phone_number === item.phone_number
                  ? {
                    phone_number: phone.phone_number,
                    has_whatsapp: hasWhatsapp === 'YES',
                    talks_with_customer: talksWithCustomer === 'YES',
                  }
                  : phone
              )),
            },
            customerInteractionId,
          },
        });
        handleClose();
      }
    } catch (error) {
      toast.error('Erro inesperado ao reportar o número de telefone.');
      datadogRum.addError(new Error('Fail to report phone number'), {
        error,
        params,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root
      size="md"
      placement="center"
      open
      onOpenChange={handleClose}
      closeOnInteractOutside={false}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>
                Reportar número
                {' '}
                <strong>{formatPhoneNumber(item.phone_number)}</strong>
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <Text mb={4} fontWeight="semibold">
                Tem WhatsApp?
              </Text>
              <RadioGroup.Root
                colorPalette="argos"
                onValueChange={(value) => setHasWhatsapp(value.value as string)}
                value={hasWhatsapp}
              >
                <HStack gap="6">
                  {RADIO_ITEMS.map((item) => (
                    <RadioGroup.Item key={item.value} value={item.value}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </HStack>
              </RadioGroup.Root>
              
              <Text my={4} fontWeight="semibold">
                Pertence ao cliente?
              </Text>
              <RadioGroup.Root
                colorPalette="argos"
                onValueChange={(value) => setTalksWithCustomer(value.value as string)}
                value={talksWithCustomer}
              >
                <HStack gap="6">
                  {RADIO_ITEMS.map((item) => (
                    <RadioGroup.Item key={item.value} value={item.value}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </HStack>
              </RadioGroup.Root>

            </Dialog.Body>

            <Dialog.Footer>
              <Button
                variant="outline"
                disabled={loading}
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button
                bg="yellow.600"
                color="white"
                onClick={onReportClick}
                loading={loading}
              >
                Reportar
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ReportPhoneNumberDialog;
